/**
 * Typed wrappers for the NIPOST Pay endpoints that aren't core wallet
 * primitives — airtime, bills, debit-card requests, disbursements.
 *
 * Wired endpoints (live on service-financial-services, port 3110):
 *   - POST /v1/wallet/airtime               — purchase airtime (debits wallet)
 *   - GET  /v1/wallet/bill-providers        — list active bill providers
 *   - POST /v1/wallet/bill-payments         — pay a bill (debits wallet)
 *   - POST /v1/wallet/cards/requests        — request a debit card
 *   - GET  /v1/wallet/cards/requests        — list customer's card requests
 *   - GET  /v1/wallet/disbursements         — operator-side batch list
 *
 * RBAC: every endpoint is `@OperatorScoped` and requires permissions like
 * `Airtime:purchase`, `BillPayment:create`, `DebitCardRequest:create`,
 * `Disbursement:read`. Citizens lack these today (Phase 1c). Pages render
 * fully and surface the keycloak-deferred notice on 401/403.
 *
 * Disbursements: there is no `/disbursements?recipientId=me` filter today
 * (the controller is operator-side only). The recipient view ships a
 * deferred notice; the read path will land in the citizen-portal follow-up
 * once a `me`-scoped endpoint is added.
 */
import {
  requestJson,
  type RequestJsonOptions,
} from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

// ---------------------------------------------------------------------------
// Airtime
// ---------------------------------------------------------------------------

export type AirtimeNetwork = 'MTN' | 'AIRTEL' | 'GLO' | '9MOBILE';

export interface PurchaseAirtimeRequest {
  /** walletId is no longer sent — the citizen endpoint resolves the wallet from JWT. */
  readonly walletId?: string;
  readonly network: AirtimeNetwork;
  readonly msisdn: string;
  readonly amount: string;
  readonly currency?: string;
  readonly idempotencyKey?: string;
}

export interface AirtimePurchaseResponse {
  readonly transactionId: string;
  readonly txnNumber: string;
  readonly walletId: string;
  readonly walletBalance: string;
  readonly amount: string;
  readonly currency: string;
  readonly network: string;
  readonly msisdn: string;
  readonly providerReference: string | null;
}

export interface TierUpgradeRequest {
  readonly nin: string;
}

export interface TierUpgradeResponse {
  readonly requestId: string;
  readonly status: 'APPROVED' | 'AUTO_APPROVED' | 'REJECTED' | 'NEEDS_REVIEW' | 'PENDING_REVIEW';
  readonly currentTier: string;
  readonly targetTier: string;
  readonly ninVerifiedName: string | null;
  readonly reason: string | null;
}

// ---------------------------------------------------------------------------
// Bills
// ---------------------------------------------------------------------------

export interface BillProvider {
  readonly id: string;
  readonly code: string;
  readonly category: string;
  readonly displayName: string;
  readonly inputSchema: unknown;
  readonly active: boolean;
}

export interface ListBillProvidersResponse {
  readonly items: readonly BillProvider[];
}

export interface PayBillRequest {
  readonly walletId: string;
  readonly providerCode: string;
  readonly amount: string;
  readonly currency?: string;
  readonly inputData: Record<string, string | number>;
  readonly idempotencyKey?: string;
}

export interface BillPaymentResponse {
  readonly id: string;
  readonly walletTransactionId: string;
  readonly providerCode: string;
  readonly inputData: unknown;
  readonly providerRef: string | null;
  readonly providerResult: unknown;
  readonly status: string;
  readonly failureReason: string | null;
}

// ---------------------------------------------------------------------------
// Debit-card requests
// ---------------------------------------------------------------------------

export interface CreateDebitCardRequestBody {
  readonly walletId: string;
  readonly embossedName: string;
  readonly deliveryAddress: Record<string, unknown>;
}

export interface DebitCardRequestResponse {
  readonly id: string;
  readonly requestNumber: string;
  readonly walletId: string;
  readonly customerId: string;
  readonly deliveryAddress: unknown;
  readonly status: string;
  readonly failureReason: string | null;
  readonly partnerRef: string | null;
  readonly approvedBy: string | null;
  readonly approvedAt: string | null;
  readonly deliveredAt: string | null;
  readonly createdBy: string;
  readonly createdAt: string;
}

export interface ListDebitCardRequestsResponse {
  readonly items: readonly DebitCardRequestResponse[];
}

// ---------------------------------------------------------------------------
// Disbursements (operator-side; recipient view is deferred)
// ---------------------------------------------------------------------------

export interface DisbursementBatchResponse {
  readonly id: string;
  readonly programmeName: string;
  readonly fundingSource: string;
  readonly totalAmount: string;
  readonly recipientCount: number;
  readonly status: string;
  readonly approvedBy: string | null;
  readonly approvedAt: string | null;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly createdBy: string;
  readonly createdAt: string;
}

export interface ListDisbursementBatchesResponse {
  readonly items: readonly DisbursementBatchResponse[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

// ---------------------------------------------------------------------------
// Wire helpers
// ---------------------------------------------------------------------------

type CallOpts = Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & {
  readonly bearerToken?: string;
};

function bearerOf(opts: CallOpts): string | undefined {
  return opts.bearerToken ?? getCustomerToken() ?? undefined;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export async function purchaseAirtime(
  body: PurchaseAirtimeRequest,
  options: CallOpts = {},
): Promise<AirtimePurchaseResponse> {
  // Omit walletId — citizen endpoint resolves wallet from JWT sub
  const { walletId: _walletId, ...rest2 } = body;
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<AirtimePurchaseResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/wallet/wallets-mine/airtime-purchases',
    body: rest2,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function requestKycTierUpgrade(
  body: TierUpgradeRequest,
  options: CallOpts = {},
): Promise<TierUpgradeResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<TierUpgradeResponse>({
    method: 'POST',
    baseUrl: env.kycBaseUrl,
    path: '/v1/kyc/tier-upgrade-requests',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function listBillProviders(
  options: CallOpts & { readonly active?: boolean } = {},
): Promise<ListBillProvidersResponse> {
  const { bearerToken: _ignore, active, ...rest } = options;
  const suffix = active === undefined ? '' : `?active=${active ? 'true' : 'false'}`;
  return requestJson<ListBillProvidersResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: `/v1/wallet/bill-providers${suffix}`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function payBill(
  body: PayBillRequest,
  options: CallOpts = {},
): Promise<BillPaymentResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<BillPaymentResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/wallet/bill-payments',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function createDebitCardRequest(
  body: CreateDebitCardRequestBody,
  options: CallOpts = {},
): Promise<DebitCardRequestResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<DebitCardRequestResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/wallet/cards/requests',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function listDebitCardRequests(
  customerId: string,
  options: CallOpts = {},
): Promise<ListDebitCardRequestsResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<ListDebitCardRequestsResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: `/v1/wallet/cards/requests?customerId=${encodeURIComponent(customerId)}`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

/**
 * Operator-side disbursement batch listing. There is currently no
 * `?recipientId=me` filter, so a citizen will see either an empty list
 * (if scope filtering returns nothing) or a 401/403. The
 * SocialDisbursementPage renders the deferred-endpoint notice in either
 * case.
 */
export async function listDisbursements(
  options: CallOpts & { readonly page?: number; readonly pageSize?: number } = {},
): Promise<ListDisbursementBatchesResponse> {
  const { bearerToken: _ignore, page, pageSize, ...rest } = options;
  const qs = new URLSearchParams();
  if (page !== undefined) qs.set('page', String(page));
  if (pageSize !== undefined) qs.set('pageSize', String(pageSize));
  const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : '';
  return requestJson<ListDisbursementBatchesResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: `/v1/wallet/disbursements${suffix}`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}
